import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import { Employee } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Info } from 'lucide-react';
import { useEmployeeDragDrop } from '@/context/EmployeeSelectionContext';
import { EmployeeSelectionService } from '@/lib/employees/EmployeeSelectionService';
import { EmployeePlanProgress } from '@/components/employees/EmployeePlanProgress';
import { EmployeePlanFulfillment } from '@/hooks/employees/usePlanFulfillment';
import EmployeeDetailModal from '@/components/employees/EmployeeDetailModal';
import { useBusinessContext } from '@/context/BusinessContext';

interface DraggableEmployeeCardProps {
  employee: Employee;
  fulfillment?: EmployeePlanFulfillment;
  onDragStart?: (employee: Employee) => void;
  onDragEnd?: () => void;
}

const DraggableEmployeeCard: React.FC<DraggableEmployeeCardProps> = ({
  employee,
  fulfillment,
  onDragStart,
  onDragEnd,
}) => {
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const { selectedBusinessId } = useBusinessContext();

  // Use unified drag & drop context
  const { startDrag, stopDrag } = useEmployeeDragDrop();

  // Check if employee can be assigned to shifts using centralized business logic
  const canAssignToShift = EmployeeSelectionService.canAssignToShift(employee);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'EMPLOYEE',
    item: () => {
      // Transform employee to standard selection format
      const standardEmployee = EmployeeSelectionService.transformToStandardFormat(employee);
      
      // Update unified context state
      startDrag(standardEmployee, 'shift-assignment');
      onDragStart?.(employee);
      
      return {
        employeeId: employee.id,
        employeeName: employee.name,
        employee: employee,
      };
    },
    end: () => {
      // Clear unified context state
      stopDrag();
      onDragEnd?.();
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    canDrag: canAssignToShift, // Use centralized business logic
  }), [employee, onDragStart, onDragEnd, startDrag, stopDrag, canAssignToShift]);

  // Get role display name
  const roleDisplay = employee.roleInfo?.name || 'No Role';

  return (
    <div
      ref={drag}
      className={`
        p-2 border rounded transition-all duration-200
        ${isDragging ? 'opacity-50' : 'opacity-100'}
        ${canAssignToShift 
          ? 'cursor-grab border bg-muted/20' 
          : 'cursor-not-allowed border bg-gray-50'
        }
      `}
      title={canAssignToShift 
        ? 'Available for assignment'
        : 'Employee cannot be assigned to shifts'
      }
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <div className="flex flex-col min-w-0 flex-1">
            <span className="font-medium text-xs truncate">
              {employee.name}
            </span>
            <div className="flex items-center gap-1">
              <Badge
                variant="outline"
                className={`text-[10px] h-4 px-1 py-0 leading-none w-fit ${
                  canAssignToShift ? '' : 'opacity-50'
                }`}
              >
                {roleDisplay}
              </Badge>
              {!canAssignToShift && (
                <AlertTriangle className="h-3 w-3 text-yellow-500" />
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              setDetailModalOpen(true);
            }}
            title="View employee details"
          >
            <Info className="h-3.5 w-3.5" />
          </Button>
        </div>
        {fulfillment && (
          <EmployeePlanProgress fulfillment={fulfillment} compact showTooltip />
        )}
      </div>

      <EmployeeDetailModal
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        employee={employee}
        businessId={selectedBusinessId}
      />
    </div>
  );
};

export default DraggableEmployeeCard;